import { useMemo } from "react";
import { VStack } from "@design-system/VStack/VStack";
import {
    resolveAnatomyBindings,
    type AnatomySize,
    type AnatomyStateId,
    type AnatomyVariant,
    type ComponentAnatomySpec,
} from "./componentAnatomy";
import { ComponentAnatomyLegend } from "./ComponentAnatomyLegend";
import { ComponentAnatomySchematic } from "./ComponentAnatomySchematic";
import type { AnatomyPartHighlight } from "./componentAnatomyParts";
import type { AvatarSchematicContent } from "./ComponentAnatomySchematicAvatar";
import type { FieldSchematicContent } from "./ComponentAnatomySchematicField";
import type { InputSchematicContent } from "./ComponentAnatomySchematicInput";
import type { KickLogoSchematicContent } from "./ComponentAnatomySchematicKickLogo";
import type { PickerSchematicContent } from "./ComponentAnatomySchematicPicker";
import type { SwitchSchematicContent } from "./ComponentAnatomySchematicSwitch";
import type { TagSchematicContent } from "./ComponentAnatomySchematicTag";
import type { TagsInputSchematicContent } from "./ComponentAnatomySchematicTagsInput";
import type { TextAreaSchematicContent } from "./ComponentAnatomySchematicTextArea";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * @purpose The x-ray itself: the schematic on its plate, the selector chain it
 * stands for, and the legend of every token bound in the selected state. Owns no
 * state — `useAnatomyMode` holds the selection so the playground's own controls
 * can drive it.
 */

/**
 * Stands in for the diagram when the playground can reach a combination this
 * spec leaves unmined, so the mode explains the gap rather than drawing an
 * invented one.
 */
export const AnatomyCombinationGap = ({
    spec,
    missing,
}: {
    spec: ComponentAnatomySpec;
    missing: string;
}) => (
    <div className={styles.gap}>
        <VStack gap={8}>
            <h3 className={styles.gapTitle}>
                {`No mined bindings for ${missing}`}
            </h3>
            <p className={styles.gapBody}>
                {`The ${spec.label} anatomy does not cover this combination yet, and inventing it would misread the stylesheet.`}
            </p>
        </VStack>
    </div>
);

export const ComponentAnatomyView = ({
    spec,
    variant,
    size,
    stateId,
    highlight,
    tagContent,
    fieldContent,
    inputContent,
    textAreaContent,
    pickerContent,
    tagsInputContent,
    avatarContent,
    kickLogoContent,
    switchContent,
}: {
    spec: ComponentAnatomySpec;
    variant: AnatomyVariant;
    size?: AnatomySize;
    stateId: AnatomyStateId;
    highlight: AnatomyPartHighlight;
    tagContent?: TagSchematicContent;
    fieldContent?: FieldSchematicContent;
    inputContent?: InputSchematicContent;
    textAreaContent?: TextAreaSchematicContent;
    pickerContent?: PickerSchematicContent;
    tagsInputContent?: TagsInputSchematicContent;
    avatarContent?: AvatarSchematicContent;
    kickLogoContent?: KickLogoSchematicContent;
    switchContent?: SwitchSchematicContent;
}) => {
    const state =
        spec.states.find((entry) => entry.id === stateId) ?? spec.states[0];

    const bindings = useMemo(
        () =>
            resolveAnatomyBindings({ spec, variant, size, stateId: state.id }),
        [spec, variant, size, state.id],
    );

    return (
        <div className={styles.layout}>
            <VStack gap={12}>
                <div className={styles.plate}>
                    <ComponentAnatomySchematic
                        schematic={spec.schematic}
                        variantId={variant.id}
                        bindings={bindings}
                        highlight={highlight}
                        tagContent={tagContent}
                        fieldContent={fieldContent}
                        inputContent={inputContent}
                        textAreaContent={textAreaContent}
                        pickerContent={pickerContent}
                        tagsInputContent={tagsInputContent}
                        avatarContent={avatarContent}
                        kickLogoContent={kickLogoContent}
                        switchContent={switchContent}
                    />
                </div>
                {/* Sizes are declared before variants in these sheets, so the
                    chain reads in the order the cascade resolves it. */}
                <code className={styles.selectorChain}>
                    {`${spec.rootSelector}${size?.selector ?? ""}${variant.selector}${state.selector}`}
                </code>
            </VStack>
            <ComponentAnatomyLegend
                spec={spec}
                bindings={bindings}
                highlight={highlight}
            />
        </div>
    );
};
